import { makeAutoObservable } from 'mobx';
import { getAnalytics, setUserId, logEvent, setUserProperties, settings } from "firebase/analytics";
import { v4 as uuidv4 } from 'uuid';
import { firebaseConfig } from '../firebase.config';

export class AnalyticsStore {
  fbAnalyticsStore = new FBAnalytics();

  constructor() {
    makeAutoObservable(this);
  }

  /** Lets the Meta server copies of pixel events identify the signed-in user (see FBAnalytics.sendServerCopy). */
  setIdTokenProvider = (provider: () => Promise<string | undefined>) => {
    this.fbAnalyticsStore.setIdTokenProvider(provider);
  }

  getAnalyticsId = (email: string) => {
    return btoa(email);
  }

  updateAnalyticsId = (email: string) => {
    const analyticsId = this.getAnalyticsId(email);
    setUserId(getAnalytics(), analyticsId);

    this.fbAnalyticsStore.updateAnalyticsId(email);
  }

  updateUserProps = (email: string, gender?: string, dob?: number) => {
    setUserId(getAnalytics(), this.getAnalyticsId(email));
    setUserProperties(getAnalytics(), {
      'gender': gender,
      'dob': dob ? (new Date(dob)).toDateString() : null
    });

    this.fbAnalyticsStore.updateUserProps(email, gender, dob);
  }

  trackSignUp = (method?: 'email') => {
    logEvent(getAnalytics(), 'sign_up', { 'method': method });
    logEvent(getAnalytics(), 'conversion', {
      'send_to': 'AW-16469123865/Z8N-CPHE_pQaEJnGi609', 'value': 0,
      'currency': 'CAD',
      'action': 'sign_up',
      'method': method
    });
    this.fbAnalyticsStore.logEvent(true, 'Signup', { method });
  }

  trackLogin = () => {
    logEvent(getAnalytics(), 'login');
    this.fbAnalyticsStore.logEvent(true, 'Login');
  }

  trackSearch = (query: string) => {
    logEvent(getAnalytics(), 'search', {
      'search_term': query
    });
    this.fbAnalyticsStore.logEvent(false, 'Search', { search_string: query, });
  }

  trackPage = (path: string) => {
    logEvent(getAnalytics(), 'page_view', {
      'path': path
    });
    logEvent(getAnalytics(), 'conversion', {
      'send_to': 'AW-16469123865/Z8N-CPHE_pQaEJnGi609', 'value': 0,
      'currency': 'CAD'
    });
    this.fbAnalyticsStore.trackPageView();
  }

  trackBillPayment = (amount: number, method: string, type: string) => {
    logEvent(getAnalytics(), 'bill_payment', {
      'amount': amount,
      'method': method,
      'bill_type': type
    });

    this.fbAnalyticsStore.logEvent(true, 'BillPayment', { amount, method, 'bill_type': type });
  }

  trackCreateListing = (listingId: string, city: string, province: string, isShared: boolean) => {
    logEvent(getAnalytics(), 'listing_create', {
      'listing_id': listingId,
      city, province,
      'is_shared_space': isShared
    });

    this.fbAnalyticsStore.logEvent(true, 'listing_create', {
      'listing_id': listingId,
      city, province,
      'is_shared_space': isShared
    });
  }

  trackCreateLease = (listingId: string, city: string, province: string, rent: number, startDate: Date, endDate: Date) => {
    logEvent(getAnalytics(), 'lease_create', {
      'listing_id': listingId,
      city, province, 'rent_amount': rent,
      'start_date': startDate,
      'end_date': endDate,
    });

    this.fbAnalyticsStore.logEvent(true, 'lease_create', {
      'listing_id': listingId,
      city, province, 'rent_amount': rent,
      'start_date': startDate,
      'end_date': endDate,
    });
  }

  trackLeaseSigned = (listingId: string, leaseId: string) => {
    logEvent(getAnalytics(), 'lease_sign', {
      'listing_id': listingId,
      'lease_id': leaseId
    });

    this.fbAnalyticsStore.logEvent(true, 'lease_sign', {
      'listing_id': listingId,
      'lease_id': leaseId
    });
  }
}



const readCookie = (name: string): string | undefined =>
  document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`))
    ?.slice(name.length + 1) || undefined;

/** Meta's click ID: the _fbc cookie, or built from ?fbclid= before the pixel has set the cookie. */
const metaClickId = (): string | undefined => {
  const cookie = readCookie('_fbc');
  if (cookie) return cookie;
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  return fbclid ? `fb.1.${Date.now()}.${fbclid}` : undefined;
};

class FBAnalytics {
  private idTokenProvider?: () => Promise<string | undefined>;

  constructor() {
    makeAutoObservable(this);
  }

  setIdTokenProvider = (provider: () => Promise<string | undefined>) => {
    this.idTokenProvider = provider;
  }

  /**
   * PageView in the browser and, with the same event ID, from the server (Meta Conversions API, publicApi
   * /meta/events), so Meta can deduplicate the pair and still count views whose browser event was blocked.
   */
  trackPageView = () => {
    if (!(window as any).fbq || !(window as any).fbEventId) return;
    const eventId = uuidv4();
    try {
      (window as any).fbq('track', 'PageView', {}, { eventID: eventId });
    } catch (err) { console.log('Unable to log Meta analytics', err) }
    this.sendServerCopy('PageView', eventId);
  }

  private sendServerCopy = async (eventName: string, eventId: string) => {
    if (!firebaseConfig.cloudFunctionPublicApiUrl) return;
    try {
      const token = await this.idTokenProvider?.().catch(() => undefined);
      await fetch(`${firebaseConfig.cloudFunctionPublicApiUrl}/meta/events`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          eventName,
          eventId,
          eventSourceUrl: window.location.href,
          eventTime: Math.floor(Date.now() / 1000),
          fbp: readCookie('_fbp'),
          fbc: metaClickId(),
        }),
      });
    } catch (err) { console.log('Unable to send Meta server event', err) }
  }

  updateAnalyticsId = (email: string) => {
    try {
      (window as any).fbq && (window as any).fbq('init', (window as any).fbEventId, { em: email });
    } catch (err) { console.log('Unable to init Meta analytics', err) }
  }

  updateUserProps = (email: string, gender?: string, dob?: number) => {
    try {
      if ((window as any).fbq && (window as any).fbEventId) {
        const fbMeta: any = { em: email };

        // Meta accepts only "m" or "f": leave any other gender out instead of guessing.
        const g = gender?.trim().toLowerCase();
        if (g === 'm' || g === 'male') fbMeta.ge = 'm';
        else if (g === 'f' || g === 'female') fbMeta.ge = 'f';

        if (dob) {
          const d = new Date(dob);
          const m = (d.getMonth() + 1).toString().padStart(2, '0')
          const day = (d.getDate()).toString().padStart(2, '0')
          fbMeta.db = `${d.getFullYear()}${m}${day}`;
        }

        (window as any).fbq('init', (window as any).fbEventId, fbMeta);
      }
    } catch (err) { console.log('Unable to init Meta analytics', err) }
  }

  logEvent = (isCustom: boolean, eventName: string, meta: any = {}) => {
    if (!(window as any).fbq) return;
    try {
      const md = { ...meta }

      if (isCustom) {
        (window as any).fbq('trackCustom', eventName, md);
      } else {
        (window as any).fbq('track', eventName, md);
      }
    } catch (err) { console.log('Unable to log Meta analytics', err) }
  }
}